import React, { useState } from 'react';
import { set } from 'dot-prop-immutable';
import { toolFormAttr, toolCollapseAttr, getLocale } from '@utils/util';
import { SimpleForm as Form, Collapse, Switch, Input, Select, InputNumber, Divider, Tooltip } from '@whalecloud/fdx';
import CustomInput from '@whalecloud/page-builder-base-components/components/CustomInput';
import CommonItemSettings from '@whalecloud/page-builder-base-components/components/ElementSettings/CommonItemSettings';

const { Panel } = Collapse;

const acceptOpts = [
  { label: 'Any', value: '*' },
  { label: 'Picture', value: 'image/*' },
  { label: 'Video', value: 'video/*' },
  { label: 'Audio', value: 'audio/*' },
  { label: 'Excel', value: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel' },
  { label: 'Word', value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword' },
  { label: 'PDF', value: 'application/pdf' },
  { label: 'TXT', value: 'text/plain' },
  { label: 'XML', value: 'text/xml,application/xml' },
  { label: 'ZIP', value: 'application/zip,application/x-zip-compressed' },
];

const valueKey = 'data.formItemSettings';

const UploadSettings = (props: any) => {
  const messages = getLocale();

  const { updateElement, element } = props;
  const { data: { formItemSettings = {} } = {} } = element;

  const {
    name = 'file',
    accept,
    action,
    download,
    preview,
    directory = false,
    withCredentials = false,
    multiple = false,
    showUploadList = true,
    listType = 'text',
    buttonText = messages.PAGEBUIDER_SETTINGS_CLICK_TO_UPLOAD,
    acceptOptions,
    successMessage = messages.PAGEBUIDER_SETTINGS_UPLOAD_SUCCESS_DEFAULT,
    errorMessage = messages.PAGEBUIDER_SETTINGS_UPLOAD_ERROR_DEFAULT,
    failMessage = messages.PAGEBUIDER_SETTINGS_UPLOAD_FAILD_DEFAULT,
    fileIdName,
    fileSize,
    showError,
  } = formItemSettings;
  const initialValues = {
    name,
    accept,
    action,
    download,
    preview,
    directory,
    withCredentials,
    multiple,
    showUploadList,
    listType,
    buttonText,
    successMessage,
    errorMessage,
    failMessage,
    fileIdName,
    fileSize,
    showError
  };

  const [option, setOption] = useState('');
  const [optionArr, setOptionArr] = useState(acceptOptions || acceptOpts);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const onOptionChange = (event: any) => {
    setOption(event.target.value);
    setTooltipVisible(false);
  };

  const addItem = () => {
    if (option) {
      const optionObj = {
        label: option,
        value: option,
      };
      const newArr = [...optionArr, optionObj];
      setOptionArr(newArr);
      const newElement = set(element, `${valueKey}.acceptOptions`, newArr);
      updateElement({
        element: newElement,
      });
    } else {
      setTooltipVisible(true);
    }
  };

  const onFormChange = (obj: Object) => {
    Object.keys(obj).forEach((key: string) => {
      const propName = `${valueKey}.${key}`;
      const newElement = set(element, propName, obj[key]);
      updateElement({
        element: newElement,
      });
    });
  };

  return (
    <Collapse {...toolCollapseAttr} defaultActiveKey={['1', '2']}>
      <Panel header={messages.PAGEBUIDER_SETTINGS} key="1">
        <CommonItemSettings defaultValueHide allowClearHide placeholderHide />
        <Form {...toolFormAttr} initialValues={initialValues} onValuesChange={onFormChange}>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_ACCEPT} name="accept">
            <Select
              style={{ width: '100%' }}
              placeholder={messages.PAGEBUIDER_SETTINGS_PLS_SELECT_FILE_TYPE}
              dropdownRender={menu => (
                <div>
                  {menu}
                  <Divider style={{ margin: '4px 0' }} />
                  <div style={{ display: 'flex', flexWrap: 'nowrap', padding: 8, alignItems: 'center' }}>
                    <Tooltip title={messages.PAGEBUIDER_SETTINGS_PLS_INPUT_ACCEPT_TYPE} visible={tooltipVisible}>
                      <Input style={{ flex: 'auto' }} value={option} onChange={onOptionChange} />
                    </Tooltip>
                    <a
                      style={{ flex: 'none', padding: '8px', display: 'block', cursor: 'pointer' }}
                      onClick={addItem}
                    >
                      {messages.PAGEBUIDER_SETTINGS_ADD_ITEM}
                    </a>
                  </div>
                </div>
              )}
              options={optionArr}
            />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_FILESIZE} name="fileSize">
            <InputNumber
              min={0}
              formatter={value => `${value}M`}
              parser={value => value.replace('M', '')}
            />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_NAME} name="name">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_PREVIEW_WIDTH} name="previewWidth">
            <CustomInput inputType="inputNumber" inputProps={{ min: 0 }} />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_PREVIEW_HEIGHT} name="previewHeight">
            <CustomInput inputType="inputNumber" inputProps={{ min: 0 }} />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_WITH_CREDENTIALS} name="withCredentials" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_DIRECTORY} name="directory" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_MULTIPLE} name="multiple" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_SHOWERROR} name="showError" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_BUTTON_TEXT} name="buttonText">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_SUCCESS} name="successMessage">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_ERROR} name="errorMessage">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_FAILD} name="failMessage">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_ID_NAME} name="fileIdName">
            <CustomInput />
          </Form.Item>
        </Form>
      </Panel>
      <Panel header={messages.PAGEBUIDER_SETTINGS_FORM_TABS_URL} key="2">
        <Form {...toolFormAttr} initialValues={initialValues} onValuesChange={onFormChange}>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_UPLOAD_URL} name="action">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_DOWNLOAD_URL} name="download">
            <CustomInput />
          </Form.Item>
          <Form.Item label={messages.PAGEBUIDER_SETTINGS_PREVIEW_URL} name="preview">
            <CustomInput />
          </Form.Item>
        </Form>
      </Panel>
    </Collapse>
  );
};

export default UploadSettings;
